import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  Alert,
  Button,
  Card,
  Group,
  Skeleton,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { IconArrowLeft, IconCheck } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { trpc } from '../../lib/trpc';
import type { TemplateButton } from '../../../../api/src/modules/whatsapp/schema';

/**
 * Per Meta v23+ rules, only `body`, `footer`, and `buttons` of an APPROVED
 * template can be edited. Header / category / language are immutable —
 * vendors must submit a new template for those changes.
 */
function TemplateEdit() {
  const { name } = useParams({ from: '/vendor/templates/$name/edit' });
  const navigate = useNavigate();
  const q = trpc.whatsapp.templates.getByName.useQuery({ name });
  const update = trpc.whatsapp.templates.update.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Edit submitted to Meta', color: 'green' });
      navigate({ to: '/vendor/templates/$name', params: { name } });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const row = q.data?.[0];
  const [bodyText, setBodyText] = useState('');
  const [footerText, setFooterText] = useState('');
  const [buttons, setButtons] = useState<TemplateButton[]>([]);

  useEffect(() => {
    if (!row) return;
    setBodyText(row.bodyText ?? '');
    setFooterText(row.footerText ?? '');
    try {
      const parsed = row.buttonsJson ? (JSON.parse(row.buttonsJson) as unknown) : [];
      if (Array.isArray(parsed)) setButtons(parsed as TemplateButton[]);
    } catch {
      /* ignore */
    }
  }, [row]);

  if (q.isPending) return <Skeleton height={400} />;
  if (!row || row.status !== 'APPROVED')
    return (
      <Stack>
        <Alert color="red">
          Only APPROVED templates can be edited. This one is in status {row?.status ?? '—'}.
        </Alert>
        <Button
          renderRoot={(props) => <Link to="/vendor/templates/$name" params={{ name }} {...props} />}
          leftSection={<IconArrowLeft size={14} />}
        >
          Back
        </Button>
      </Stack>
    );

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group>
          <Button
            variant="subtle"
            renderRoot={(props) => (
              <Link to="/vendor/templates/$name" params={{ name }} {...props} />
            )}
            leftSection={<IconArrowLeft size={14} />}
          >
            Back
          </Button>
        </Group>
        <Title order={2}>Edit — {name}</Title>
        <Alert color="blue">
          Body, footer, and buttons can be edited. Header / category / language are immutable per
          Meta — submit a new template for those changes.
        </Alert>
        <Card>
          <Stack>
            <Textarea
              label="Body"
              autosize
              minRows={4}
              maxLength={1024}
              value={bodyText}
              onChange={(e) => setBodyText(e.currentTarget.value)}
            />
            <TextInput
              label="Footer"
              maxLength={60}
              value={footerText}
              onChange={(e) => setFooterText(e.currentTarget.value)}
            />
            {buttons.length > 0 && (
              <Stack gap={4}>
                <Text size="sm" fw={500}>
                  Buttons (label edits only — type / target are pinned)
                </Text>
                {buttons.map((b, i) => (
                  <Group key={i}>
                    <Text size="xs" c="dimmed" w={100}>
                      {b.type}
                    </Text>
                    {'text' in b && (
                      <TextInput
                        size="xs"
                        value={b.text ?? ''}
                        onChange={(e) =>
                          setButtons((prev) =>
                            prev.map((p, idx) =>
                              idx === i
                                ? ({ ...p, text: e.currentTarget.value } as TemplateButton)
                                : p,
                            ),
                          )
                        }
                        style={{ flex: 1 }}
                      />
                    )}
                  </Group>
                ))}
              </Stack>
            )}
            <Group justify="end">
              <Button
                variant="default"
                renderRoot={(props) => (
                  <Link to="/vendor/templates/$name" params={{ name }} {...props} />
                )}
              >
                Cancel
              </Button>
              <Button
                leftSection={<IconCheck size={14} />}
                loading={update.isPending}
                onClick={() =>
                  update.mutate({
                    templateId: row.id,
                    body: { text: bodyText },
                    footer: footerText ? { text: footerText } : undefined,
                    buttons: buttons.length > 0 ? buttons : undefined,
                  })
                }
              >
                Save
              </Button>
            </Group>
          </Stack>
        </Card>
      </Stack>
    </motion.div>
  );
}

export const Route = createFileRoute('/vendor/templates/$name/edit')({ component: TemplateEdit });
